import { type ReactNode, useState } from "react";
import type {
    DataGridListEditBase,
    DataGridListEditLayout,
    DataGridListEditorPopup,
    DataGridListEditorProps,
    DataGridListTriggerContent,
} from "../../../columns/listEditors";
import { ListEditTrigger } from "../ListEditTrigger";
import { runListCommit, toCloseAndCommit } from "../listEditActions";

/** `col.edit.custom`: a widget that owns its popup (a grouped multi-select, a category tree). */
export interface DataGridListCustomEdit<TData, TValue>
    extends DataGridListEditBase<TData, TValue>, DataGridListEditLayout {
    popup: DataGridListEditorPopup;
    /**
     * Runs as a component inside the cell (and the row provider), so it may
     * call hooks. The widget names its own popup (`aria-label` with
     * `columnLabel`); the grid can't reach it.
     */
    render: (props: {
        row: TData;
        value: TValue;
        /** The column's name, for the popup's `aria-label`. */
        columnLabel: string;
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
        /** Spread onto `ListEditTrigger`, with the widget's own trigger props. */
        trigger: DataGridListTriggerContent;
        /** Closes, focuses the cell, then saves (`runListCommit`). */
        commit: (value: TValue) => void;
        /** Saves and stays open: multi-select. */
        save: (value: TValue) => void;
    }) => ReactNode;
}

interface CustomListEditorProps<
    TData,
    TValue,
> extends DataGridListEditorProps<TData> {
    edit: DataGridListCustomEdit<TData, TValue>;
}

/** The column's `render`, as its own component so it may call hooks. */
const CustomListBody = <TData, TValue>({
    row,
    columnLabel,
    trigger,
    isOpen,
    onOpenChange,
    onCloseAndCommit,
    edit,
}: Readonly<CustomListEditorProps<TData, TValue>>) =>
    edit.render({
        row,
        value: edit.value(row),
        columnLabel,
        isOpen,
        onOpenChange,
        trigger,
        commit: toCloseAndCommit({ edit, row, onCloseAndCommit }),
        save: (value) => runListCommit({ edit, row, value }),
    });

/**
 * `col.edit.custom`: a plain trigger until the cell first opens, so widgets
 * that fetch data don't mount in every row; the column's widget from then on.
 */
export const CustomListEditor = <TData, TValue>(
    props: Readonly<CustomListEditorProps<TData, TValue>>,
) => {
    const [hasOpened, setHasOpened] = useState(false);
    if (props.isOpen && !hasOpened) {
        setHasOpened(true);
    }
    if (!hasOpened) {
        return (
            <ListEditTrigger
                {...props.trigger}
                onClick={() => props.onOpenChange(true)}
            />
        );
    }
    return <CustomListBody {...props} />;
};
